/**
 * Cat & Mouse — the rules of a step, with no Phaser in them (specs/cat-mouse-mode.md §5).
 *
 * `CatScene` used to own these outright, which meant the one thing most worth proving could only be
 * proved by launching a browser: **that the cat can actually reach every target and get through a
 * door**. `deal.ts` promises a board is finishable, but that promise is about the board's *data* —
 * it says nothing about whether the rules that read the board agree. A generator guarantee and an
 * input handler that disagree produce exactly the bug that is impossible to find by reading either.
 *
 * So the rules live here, the scene calls them, and `walk.test.ts` drives a whole run through them.
 */
import type { CatBoard, CatDoor } from './deal';
import { canStep, cellKey, routeTo, type Cell } from './board';

export interface WalkState {
  cat: Cell;
  /** Cell keys collected, in collection order. */
  taken: string[];
  fish: number;
  /** Every target found: the letters are asleep and the way out is open (§5.3.1). */
  cleared: boolean;
}

export type StepOutcome =
  /** Not adjacent, or walled off. Nothing happens at all — no flash, no sound, no penalty (§5.1). */
  | { kind: 'ignored' }
  /** Floor, an emptied cell, or any cell once the board is cleared. */
  | { kind: 'moved'; state: WalkState }
  | { kind: 'collected'; state: WalkState; glyph: string }
  /** A decoy, or a word-board letter taken out of turn: one fish, and the cat does NOT move. */
  | { kind: 'wrong'; state: WalkState };

export function initialWalk(board: CatBoard, fish: number): WalkState {
  return { cat: board.entry, taken: [], fish, cleared: false };
}

/** The letter written on a cell, by key. */
function glyphAt(board: CatBoard, key: string): string {
  const [c, r] = key.split(',').map(Number);
  return board.grid[r]?.[c] ?? '';
}

/**
 * On the word board the letters go in order; everywhere else any target will do (§7.1).
 *
 * **In order means the LETTER, not the cell.** `Boot` puts two `o`s on the grid and either of them
 * is the `o` the word wants next — they are the same letter, and a child reaching for the nearer
 * one has not made a mistake. This compared cell keys once, so the board picked one of the two `o`s
 * at deal time and charged a fish for the other: a question with no readable answer, since the two
 * cells are identical to look at. The Space Worm had this right from the start (`worm/order.ts`
 * counts glyphs, never tile identity) and this is the same rule.
 */
export function isNextTarget(board: CatBoard, state: WalkState, key: string): boolean {
  if (!board.order) return true;
  const want = board.order[state.taken.length];
  if (want === undefined) return false;
  return want === key || glyphAt(board, want) === glyphAt(board, key);
}

/**
 * Is this cell free to walk onto — costing nothing and asking nothing?
 *
 * Empty floor, a cell whose letter has already been collected, and every cell of a board that has
 * been answered (§5.3.1). Everything else is a letter that has to be decided about.
 */
export function isFree(board: CatBoard, state: WalkState, cell: Cell): boolean {
  if (state.cleared) return true;
  const glyph = board.grid[cell[1]][cell[0]];
  return glyph === '' || state.taken.includes(cellKey(cell));
}

/**
 * The cells a tap should walk the cat through, in order — the answer to "I tapped over there".
 *
 * A tap on a cell **in the cat's own row or column** walks the whole straight line to it, rather
 * than doing nothing because it was not adjacent. Tapping the cell you want is what a child does;
 * making them tap every cell in between is a tax on having understood the board.
 *
 * The walk **stops in front of the first letter in the way** and that costs nothing. Only the cell
 * that was actually TAPPED is ever decided about — so reaching for a target three cells off can
 * leave the cat short, but it can never spend a fish on a letter the child did not choose. Being
 * predictable matters more here than being clever.
 *
 * An adjacent tap is just the one-cell case of this, and a tap off the line returns nothing.
 */
export function lineSteps(board: CatBoard, state: WalkState, target: Cell): Cell[] {
  const [tc, tr] = target;
  const [cc, cr] = state.cat;
  if (tc < 0 || tr < 0 || tc >= board.cols || tr >= board.rows) return [];
  if (tc === cc && tr === cr) return [];
  if (tc !== cc && tr !== cr) return [];

  const dc = Math.sign(tc - cc);
  const dr = Math.sign(tr - cr);
  const out: Cell[] = [];
  let walked: WalkState = state;
  for (let c = cc + dc, r = cr + dr; ; c += dc, r += dr) {
    const cell: Cell = [c, r];
    out.push(cell);
    if (c === tc && r === tr) break;
    // A letter in the way ends the walk BEFORE it — the cat stops, nothing is spent.
    if (!isFree(board, walked, cell)) { out.pop(); break; }
    walked = { ...walked, cat: cell };
  }
  return out;
}

export function step(board: CatBoard, state: WalkState, target: Cell): StepOutcome {
  if (!canStep(state.cat, target, board.cols, board.rows)) return { kind: 'ignored' };

  const key = cellKey(target);

  // Empty floor, an already-emptied cell, and anything at all on a cleared board are free to walk.
  if (isFree(board, state, target)) {
    return { kind: 'moved', state: { ...state, cat: target } };
  }
  const glyph = board.grid[target[1]][target[0]];

  if (board.targets.has(key) && isNextTarget(board, state, key)) {
    const taken = [...state.taken, key];
    return {
      kind: 'collected',
      glyph,
      state: { ...state, cat: target, taken, cleared: taken.length >= board.targets.size },
    };
  }

  return { kind: 'wrong', state: { ...state, fish: Math.max(0, state.fish - 1) } };
}

/**
 * The cells that walk the cat to `door` — the paw-print route, tap by tap.
 *
 * Only on a cleared board, where every cell is free (§5.3.1), so this is a plain shortest path
 * around nothing: the same route `CatScene` lays the paw prints along, which is the point. The
 * prints say "this way", and tapping the door has to mean what they say — a door off the cat's row
 * and column used to be the one thing the prints pointed at that a tap could not reach (§5.3.2).
 *
 * Empty when the board is not cleared, when the cat is already standing at the door, or when there
 * is no route at all.
 */
export function doorSteps(board: CatBoard, state: WalkState, door: CatDoor): Cell[] {
  if (!state.cleared) return [];
  const route = routeTo([board.cols - 1, door.row], state.cat, board.cols, board.rows);
  return route ? route.slice(1) : [];
}

/**
 * The door the cat is standing at, if any.
 *
 * A door hangs off the last column, so "standing at it" is standing on that column in its row. The
 * scene checks this after every move and goes through on arrival: **walking to the door is the whole
 * interaction**. Requiring a separate tap on the door graphic was a second step nobody found — and
 * the graphic had by then shrunk to a sliver, because opening it tweens its scale and Phaser keeps
 * the hit area tied to the object (§5.3.2).
 */
export function doorUnderCat(board: CatBoard, state: WalkState): CatDoor | null {
  if (state.cat[0] !== board.cols - 1) return null;
  return board.doors.find((d) => d.row === state.cat[1]) ?? null;
}

export type DoorOutcome =
  /** The cat is not standing at this door — a tap on it does nothing (§5.1). */
  | 'not-there'
  /** Standing there, letters still missing: the mouse squeaks and it costs nothing (§5.3). */
  | 'shut'
  | 'open';

export function tryDoor(board: CatBoard, state: WalkState, door: CatDoor): DoorOutcome {
  const atDoor = state.cat[0] === board.cols - 1 && state.cat[1] === door.row;
  if (!atDoor) return 'not-there';
  return state.taken.length >= board.targets.size ? 'open' : 'shut';
}
